import React, { useState } from 'react';
import { Row, Col, Card, Form, Button, Spinner, Alert, Badge, Table, Collapse } from 'react-bootstrap';
import axios from 'axios';
import { store } from '../../../../store/store';
import segnalaMancanti from '../../../components/Util/campiMancanti';

const MARKING_LABELS = {
    accepted: { label: 'Accettata', variant: 'success' },
    rejected: { label: 'Scartata', variant: 'danger' },
    delivered: { label: 'Consegnata', variant: 'primary' },
    pending: { label: 'In attesa', variant: 'warning' },
};

const NOTIFICATION_TYPES = {
    NS: 'Scarto',
    RC: 'Consegna',
    MC: 'Mancata Consegna',
    AT: 'Attestazione',
    DT: 'Decorrenza Termini',
};

const MarkingBadge = ({ marking }) => {
    const info = MARKING_LABELS[marking] || { label: marking, variant: 'secondary' };
    return <Badge bg={info.variant}>{info.label}</Badge>;
};

const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
        return new Date(isoString).toLocaleString('it-IT', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return isoString;
    }
};

const NotificationsRow = ({ notifications }) => {
    if (!notifications || notifications.length === 0) {
        return <span className="text-muted small">Nessuna notifica</span>;
    }
    return (
        <div>
            {notifications.map((n, i) => (
                <div key={i} className="small">
                    <strong>{NOTIFICATION_TYPES[n.type] || n.type}</strong>
                    {' — '}
                    <span className="text-muted">{formatDate(n.created_at)}</span>
                </div>
            ))}
        </div>
    );
};

const FatturaRow = ({ fattura }) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <tr>
                <td>
                    <div className="small text-monospace text-muted" style={{ fontSize: '11px' }}>{fattura.uuid}</div>
                    <div className="small">{fattura.sdi_file_name}</div>
                </td>
                <td className="small">{formatDate(fattura.created_at)}</td>
                <td className="small">{fattura.document_type}</td>
                <td>
                    <div className="small"><strong>{fattura.sender?.business_name || [fattura.sender?.name, fattura.sender?.surname].filter(Boolean).join(' ')}</strong></div>
                    <div className="small text-muted">{fattura.sender?.business_vat_number_code || fattura.sender?.business_fiscal_code}</div>
                </td>
                <td>
                    <div className="small"><strong>{fattura.recipient?.business_name || [fattura.recipient?.name, fattura.recipient?.surname].filter(Boolean).join(' ')}</strong></div>
                    <div className="small text-muted">{fattura.recipient?.business_vat_number_code || fattura.recipient?.business_fiscal_code}</div>
                    {fattura.recipient?.recipient_code && (
                        <div className="small text-muted">Codice: {fattura.recipient.recipient_code}</div>
                    )}
                </td>
                <td>
                    <MarkingBadge marking={fattura.marking} />
                    {fattura.notice && (
                        <div className="small text-danger mt-1">{fattura.notice}</div>
                    )}
                </td>
                <td>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setOpen(o => !o)}
                        aria-controls={`notif-${fattura.uuid}`}
                        aria-expanded={open}
                    >
                        {open ? 'Chiudi' : 'Notifiche'}
                        {fattura.notifications && fattura.notifications.length > 0 && (
                            <Badge bg="secondary" className="ms-1">{fattura.notifications.length}</Badge>
                        )}
                    </Button>
                </td>
            </tr>
            <tr id={`notif-${fattura.uuid}`}>
                <td colSpan={7} style={{ padding: open ? undefined : 0, border: open ? undefined : 'none' }}>
                    <Collapse in={open}>
                        <div className="p-2 bg-light">
                            <NotificationsRow notifications={fattura.notifications} />
                        </div>
                    </Collapse>
                </td>
            </tr>
        </>
    );
};

const DebugFatture = () => {
    const today = new Date().toISOString().slice(0, 10);
    const firstOfMonth = today.slice(0, 7) + '-01';

    const [dateFrom, setDateFrom] = useState(firstOfMonth);
    const [dateTo, setDateTo] = useState(today);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fatture, setFatture] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        // Date mancanti o al contrario: si evidenziano i campi (set-ui «Dati mancanti»)
        const mancanti = [];
        if (!dateFrom) mancanti.push({ campo: 'debug-data-da', messaggio: 'Manca la data di inizio' });
        if (!dateTo) mancanti.push({ campo: 'debug-data-a', messaggio: 'Manca la data di fine' });
        else if (dateFrom && dateTo < dateFrom) mancanti.push({ campo: 'debug-data-a', messaggio: 'La data di fine deve essere dopo la data di inizio' });
        if (segnalaMancanti(mancanti) > 0) return;
        setError('');
        setFatture(null);
        setLoading(true);

        try {
            const token = store.getState().auth.auth.idToken;
            const basePath = process.env.REACT_APP_API_BASE_PATH.replace(/\/+$/, '');
            const url = `${basePath}/sdi/debug/invoices?dateFrom=${dateFrom}&dateTo=${dateTo}`;

            const response = await axios.get(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token,
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) {
                setFatture(response.data.data || []);
            } else {
                setError(`Errore ${response.status}: risposta inattesa dal server.`);
            }
        } catch (err) {
            if (err.response) {
                const status = err.response.status;
                if (status === 400) {
                    setError('Parametri mancanti: assicurati di aver inserito dateFrom e dateTo.');
                } else if (status === 401) {
                    setError('Token mancante o scaduto. Effettua nuovamente il login.');
                } else if (status === 500) {
                    setError('Errore di comunicazione con OpenAPI SDI (500).');
                } else {
                    setError(`Errore ${status}: ${err.response.data?.detail || 'Errore sconosciuto.'}`);
                }
            } else {
                setError('Errore di connessione al server.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Row>
            <Col lg={12}>
                <Card>
                    <Card.Header>
                        <Card.Title>Debug Fatture SDI</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <Form onSubmit={handleSearch} noValidate>
                            <Row className="align-items-end">
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label htmlFor="debug-data-da">Data da *</Form.Label>
                                        <Form.Control
                                            id="debug-data-da"
                                            type="date"
                                            value={dateFrom}
                                            onChange={e => setDateFrom(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label htmlFor="debug-data-a">Data a *</Form.Label>
                                        <Form.Control
                                            id="debug-data-a"
                                            type="date"
                                            value={dateTo}
                                            onChange={e => setDateTo(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <div className="mb-3" style={{ paddingTop: '28px' }}>
                                        <Button type="submit" variant="primary" size="sm" disabled={loading}>
                                            {loading ? <Spinner animation="border" size="sm" /> : 'Cerca'}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>

                        {error && <Alert variant="danger">{error}</Alert>}

                        {fatture !== null && (
                            <>
                                <div className="mb-2 text-muted small">
                                    {fatture.length === 0
                                        ? 'Nessuna fattura trovata nel periodo selezionato.'
                                        : `${fatture.length} fattur${fatture.length !== 1 ? 'e' : 'a'} trovat${fatture.length !== 1 ? 'e' : 'a'}.`
                                    }
                                </div>
                                {fatture.length > 0 && (
                                    <div className="table-responsive">
                                        <Table bordered hover size="sm">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>UUID / File</th>
                                                    <th>Data invio</th>
                                                    <th>Tipo</th>
                                                    <th>Mittente</th>
                                                    <th>Destinatario</th>
                                                    <th>Stato</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fatture.map(f => (
                                                    <FatturaRow key={f.uuid} fattura={f} />
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default DebugFatture;
